import zodToJsonSchema from 'zod-to-json-schema';
import { tool, jsonSchema } from 'ai';
import { wrapTraced } from 'braintrust';
import { z } from 'zod';

function makeServerTool(tool) {
  return tool;
}
function makeClientTool(tool) {
  return tool;
}
function makeFinalTool(tool) {
  return tool;
}
function makeToolComponent(component) {
  return component;
}

function extractComponents(tools) {
  return Object.fromEntries(
    tools.filter(
      (tool) => tool.component !== void 0
    ).map((tool) => [tool.name, tool.component])
  );
}
function serializeTools(tools) {
  return tools.map(({ name, description, parameters }) => ({
    name,
    description,
    parameters: zodToJsonSchema(parameters)
  }));
}
function createOnToolCall(tools) {
  const execMap = Object.fromEntries(tools.map((t) => [t.name, t.execute]));
  return async (params) => {
    if (!params) {
      console.warn(
        "createOnToolCall: params is undefined.\n\nDid you setState(createOnToolCall(...))? If so, use setState(() => createOnToolCall(...)) instead."
      );
      return Promise.resolve(void 0);
    }
    const { toolCall } = params;
    const { toolName, args } = toolCall;
    const handler = execMap[toolName];
    if (!handler) return Promise.resolve(void 0);
    return handler(args);
  };
}

async function createServerTools({
  serverTools = [],
  clientTools = [],
  disableErrorWrapping = false,
  parentSpanId,
  logger
}) {
  const tools = {};
  serverTools.forEach((t) => {
    tools[t.name] = "execute" in t ? tool({
      description: t.description,
      parameters: disableErrorWrapping ? t.parameters : createValidationBypassSchema(t.parameters),
      execute: (() => {
        let wrappedExecute = t.execute;
        if (!disableErrorWrapping) {
          wrappedExecute = wrapExecuteWithValidation(
            wrappedExecute,
            t.parameters
          );
        }
        if (logger) {
          wrappedExecute = wrapExecuteWithTracing(
            wrappedExecute,
            t.name,
            parentSpanId,
            logger
          );
        }
        return wrappedExecute;
      })()
    }) : tool({
      description: t.description,
      parameters: t.parameters
    });
  });
  clientTools.forEach((t) => {
    tools[t.name] = tool({
      description: t.description,
      parameters: jsonSchema(t.parameters)
    });
  });
  return tools;
}
function createErrorObject(error) {
  const isDevelopment = process.env.NODE_ENV === "development";
  if (error instanceof z.ZodError) {
    return {
      $error: {
        message: "Validation failed",
        issues: error.errors.map((issue) => ({
          path: issue.path.join("."),
          message: issue.message
        })),
        ...isDevelopment && { stack: error.stack }
      }
    };
  }
  if (error instanceof Error) {
    return {
      $error: {
        message: error.message,
        ...isDevelopment && { stack: error.stack }
      }
    };
  }
  return {
    $error: {
      message: typeof error === "string" ? error : "Unknown error occurred"
    }
  };
}
function createValidationBypassSchema(schema) {
  const bypass = Object.create(schema);
  bypass.parse = (data) => data;
  bypass.safeParse = (data) => ({ success: true, data });
  bypass.parseAsync = async (data) => data;
  bypass.safeParseAsync = async (data) => ({ success: true, data });
  return bypass;
}
function wrapExecuteWithTracing(execute, toolName, parentSpanId, logger) {
  if (!logger) {
    return execute;
  }
  return wrapTraced(
    async (args) => {
      const result = await execute(args);
      return result;
    },
    {
      type: "tool",
      name: `Server Tool: ${toolName}`,
      ...parentSpanId && { parent: parentSpanId }
    }
  );
}
function wrapExecuteWithValidation(execute, schema) {
  return async (args) => {
    try {
      const validatedArgs = schema.parse(args);
      return await execute(validatedArgs);
    } catch (error) {
      return createErrorObject(error);
    }
  };
}

function generateHandoffContext(copilots) {
  const copilotList = copilots.map((copilot) => {
    const paramInfo = copilot.parameters ? ` (requires parameters)` : "";
    return `- ${copilot.id}: ${copilot.description}${paramInfo}`;
  }).join("\n");
  return `Available copilots for handoff:
${copilotList}`;
}
function createCopilotRegistry(copilots) {
  const registry = {
    copilots,
    getById(id) {
      return copilots.find((c) => c.id === id);
    },
    createIdSchema() {
      const ids = copilots.map((c) => c.id);
      if (ids.length === 0) {
        throw new Error("Cannot create schema from empty registry");
      }
      const enumValues = ids;
      const description = `The ID of the copilot:

${copilots.map((c) => `* ${c.id}: ${c.name} (${c.description})`).join("\n")}`;
      return z.enum(enumValues).describe(description);
    },
    generateSystemPrompt(options) {
      const { basePrompt } = options;
      const handoffContext = generateHandoffContext(copilots);
      return basePrompt.includes("{{handoffPrompt}}") ? basePrompt.replace("{{handoffPrompt}}", handoffContext) : `${basePrompt}

${handoffContext}`;
    }
  };
  return registry;
}

const globalCopilots = [
  {
    id: "agents",
    name: "Agent Builder",
    description: "Create and edit Zapier Agents - AI-powered agents that can perform actions independently across your connected apps and services.",
    parameters: z.object({
      botId: z.string().optional().describe("The ID of the Agent to edit, blank if new Agent")
    }),
    generateUrl({ botId }) {
      if (botId) {
        return `https://agents.zapier.com/bots/${botId}/settings?tab=chat`;
      } else {
        return "https://agents.zapier.com/bots?new=true";
      }
    }
  },
  {
    id: "canvas",
    name: "Canvas Workflow Builder",
    description: "Visual workflow builder for creating and editing Zapier automations",
    generateUrl: () => {
      const zapierStaging = "https://zapier-staging.com";
      const canvasSuffix = "/app/canvas/ai";
      if (process.env.NODE_ENV === "development") {
        const canvasAddress = process.env.NEXT_PUBLIC_CANVAS_ADDRESS ? "http://localhost:3004" : zapierStaging;
        return `${canvasAddress}${canvasSuffix}`;
      } else if (process.env.VERCEL_ENV === "preview") {
        return `${zapierStaging}${canvasSuffix}`;
      } else {
        return `${process.env.NEXT_PUBLIC_ZAPIER_ORIGIN}${canvasSuffix}`;
      }
    }
  },
  {
    id: "editor",
    name: "Zap Editor",
    description: "Create and edit Zaps in the Zap Editor -  an intuitive, no-code platform for building workflow automations with Zapier.",
    parameters: z.object({
      zapId: z.string().optional().describe("The ID of the Zap, blank if creating new Zap"),
      stepId: z.string().optional().describe(
        "The ID of the step to route to (unused if Zap ID is not provided)"
      )
    }),
    generateUrl: ({ zapId, stepId }) => {
      const baseUrl = process.env.NODE_ENV === "development" && process.env.NEXT_PUBLIC_EDITOR_URL ? process.env.NEXT_PUBLIC_EDITOR_URL : process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
      if (zapId) {
        if (stepId) {
          return `${baseUrl}/editor/${zapId}/draft/${stepId}`;
        }
        return `${baseUrl}/editor/${zapId}/draft`;
      }
      return `${baseUrl}/webintent/create-zap`;
    }
  },
  {
    id: "interfaces",
    name: "Interface Builder",
    description: `Create and edit Zapier Interfaces - a powerful no-code platform for building interactive web interfaces, forms, and data collection tools. 
      Build custom pages with drag-and-drop blocks, create dynamic forms for data capture, set up user authentication and access controls, customize themes and branding and connect to Zapier workflows for automation.`,
    parameters: z.object({
      projectId: z.string().optional().describe(
        "The ID of the Interface project to edit, blank if creating new Interface"
      )
    }),
    generateUrl({ projectId }) {
      let baseUrl;
      if (process.env.NODE_ENV === "development") {
        baseUrl = "http://localhost:3000";
      } else if (process.env.VERCEL_ENV === "preview") {
        baseUrl = "https://interfaces.zapier-staging.com";
      } else {
        baseUrl = "https://interfaces.zapier.com";
      }
      const path = projectId ? `/interfaces/project/${projectId}` : `/interfaces/get-started`;
      const url = new URL(path, baseUrl);
      if (projectId) {
        url.searchParams.set("sideNavDrawer", "settings-copilot");
      }
      return url.toString();
    }
  },
  {
    id: "tables",
    name: "Table Builder",
    description: `Create and manage Zapier Tables - a flexible, no-code database that lets you store, organize, and 
      automate data. Built to seamlessly integrate with your workflows across Zapier`,
    parameters: z.object({
      tableId: z.string().describe("The ID of the Table to manage.")
    }),
    generateUrl({ tableId }) {
      const tablesStaging = "https://tables.zapier-staging.com";
      const tablesProd = "https://tables.zapier.com";
      const tablesSuffix = `/app/tables/t/${tableId}`;
      if (process.env.NODE_ENV === "development") {
        return `http://localhost:8080${tablesSuffix}`;
      } else if (process.env.VERCEL_ENV === "preview") {
        return `${tablesStaging}${tablesSuffix}`;
      } else {
        return `${tablesProd}${tablesSuffix}`;
      }
    }
  },
  {
    id: "memberhome",
    name: "Home Page",
    description: `
      Home is your personalized starting point for automation. Find relevant use cases, tools, and features tailored to your role and interests.
      What it does:
      - Shows use cases and tools based on your needs
      - Recommends next steps to get started or go deeper
      - Easily search and browse functionality
      When to use it:
      - You're new and want to explore what's possible
      - You're returning and want to pick up where you left off
      - You want inspiration or examples from other users
    `,
    generateUrl: () => {
      if (process.env.VERCEL_ENV === "preview") {
        return `https://zapier-staging.com/app/home`;
      } else {
        return `${process.env.NEXT_PUBLIC_ZAPIER_ORIGIN ?? "https://zapier-staging.com"}/app/home`;
      }
    }
  },
  {
    id: "chatbots",
    name: "Chatbots",
    description: `Chatbots is the easiest way to automate any customer interaction, helping your business scale support and generate more leads.`,
    parameters: z.object({
      chatbotId: z.string().optional().describe(
        "The ID of the Chatbot to edit, blank if creating new Chatbot"
      )
    }),
    generateUrl({ chatbotId }) {
      let baseUrl;
      if (process.env.NODE_ENV === "development") {
        baseUrl = "http://localhost:3033";
      } else if (process.env.VERCEL_ENV === "preview") {
        baseUrl = "https://zapier-staging.com";
      } else {
        baseUrl = "https://zapier.com";
      }
      const path = chatbotId ? `/app/chatbots/${chatbotId}` : `/app/chatbots/create`;
      const url = new URL(path, baseUrl);
      return url.toString();
    }
  }
];
async function getGlobalCopilotRegistry() {
  return createCopilotRegistry(globalCopilots);
}

var HandoffStatus = /* @__PURE__ */ ((HandoffStatus2) => {
  HandoffStatus2["Initializing"] = "initializing";
  HandoffStatus2["None"] = "none";
  HandoffStatus2["Loading"] = "loading";
  HandoffStatus2["Ready"] = "ready";
  HandoffStatus2["Applied"] = "applied";
  HandoffStatus2["Error"] = "error";
  return HandoffStatus2;
})(HandoffStatus || {});
const HANDOFF_TOOL_NAME = "handoff";

const commonToolParams = z.object({
  displayMessage: z.string().describe(
    "A 8-12 word message to display to the user about why we are using this tool (e.g., 'Searching for relevant apps...', 'Let's take a look at your current workflows...')"
  )
});

function pruneMessagesAfterTool(messages, toolName) {
  const prunedMessages = JSON.parse(JSON.stringify(messages));
  for (let i = prunedMessages.length - 1; i >= 0; i--) {
    const message = prunedMessages[i];
    if (message.role === "assistant" && message.parts) {
      let toolPartIndex = -1;
      for (let j = 0; j < message.parts.length; j++) {
        const part = message.parts[j];
        if (part.type === "tool-invocation" && part.toolInvocation.toolName === toolName) {
          toolPartIndex = j;
          break;
        }
      }
      if (toolPartIndex !== -1) {
        message.parts = message.parts.slice(0, toolPartIndex + 1);
        prunedMessages.splice(i + 1);
        break;
      }
    }
  }
  return prunedMessages;
}
function generateRandomUUID() {
  return crypto.randomUUID();
}

const trimStr = (str, length) => {
  if (typeof str !== "string") return str;
  if (str.length <= length) return str;
  const half = Math.floor(length / 2);
  return str.slice(0, half) + " [TRUNCATED]" + str.slice(-half);
};
const recurseTrim = (obj, length) => {
  if (typeof obj === "string") {
    return trimStr(obj, length);
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => recurseTrim(item, length));
  }
  if (obj && typeof obj === "object" && obj !== null) {
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = recurseTrim(obj[key], length);
      }
    }
    return result;
  }
  return obj;
};

export { HANDOFF_TOOL_NAME as H, makeFinalTool as a, makeServerTool as b, makeToolComponent as c, createOnToolCall as d, extractComponents as e, createErrorObject as f, createServerTools as g, commonToolParams as h, createCopilotRegistry as i, globalCopilots as j, getGlobalCopilotRegistry as k, HandoffStatus as l, makeClientTool as m, generateRandomUUID as n, pruneMessagesAfterTool as p, recurseTrim as r, serializeTools as s };
//# sourceMappingURL=recurseTrim-e9oJykUk.mjs.map
